// src/hooks/useSites.ts - Lieux / sites (multi-sites) - gestion Super Admin uniquement

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch,
  deleteField,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { Site, SiteType } from '../types/site';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from './useBusinesses';
import { useCurrentEmployee } from './useEmployees';
import {
  resolveDefaultReceivingSite,
  resolveDefaultSalesOrStockSite,
  resolveFavoriteSite,
} from '../utils/siteDefaults';
import {
  emptySiteDeleteBlockers,
  evaluateSiteDelete,
  type SiteDeleteCheckResult,
} from '../utils/siteDeleteGuards';

/** Compte rapidement s’il existe au moins un doc avec siteId (évite index composite businessId+siteId). */
const countDocsBySiteId = async (
  collectionName: string,
  siteId: string,
  context: string
): Promise<number> => {
  const snap = await getDocs(
    query(collection(db, collectionName), where('siteId', '==', siteId), limit(1))
  ).catch((err) => {
    handleFirestoreError(err, context);
    throw err;
  });
  return snap.empty ? 0 : 1;
};

/**
 * Vérifie si un lieu peut être supprimé (pas de ventes / stock / historiques liés).
 * Nettoie uniquement les références « mortes » sûres au moment du delete (favoris, clés stock 0).
 */
export const canDeleteSite = async (params: {
  siteId: string;
  businessId: string;
  allowMultiplePointOfSale?: boolean;
}): Promise<SiteDeleteCheckResult> => {
  const { siteId, businessId, allowMultiplePointOfSale } = params;
  const blockers = emptySiteDeleteBlockers();

  const siteRef = doc(db, COLLECTIONS.sites, siteId);
  const siteSnap = await getDoc(siteRef);
  if (!siteSnap.exists()) {
    return { canDelete: false, reason: 'Lieu introuvable', blockers };
  }
  const siteData = siteSnap.data();
  if (siteData.businessId !== businessId) {
    return { canDelete: false, reason: 'Lieu non autorisé pour ce business', blockers };
  }

  if (siteData.type === 'point_of_sale' && !allowMultiplePointOfSale) {
    const posSnap = await getDocs(
      query(
        collection(db, COLLECTIONS.sites),
        where('businessId', '==', businessId),
        where('type', '==', 'point_of_sale')
      )
    ).catch((err) => {
      handleFirestoreError(err, 'canDeleteSite - POS count');
      throw err;
    });
    const otherPos = posSnap.docs.filter((d) => d.id !== siteId);
    if (otherPos.length === 0) {
      blockers.isSolePointOfSale = true;
      return evaluateSiteDelete(blockers);
    }
  }

  const [
    productSales,
    prestations,
    stockMovements,
    deliveriesTo,
    deliveriesFrom,
    externalDispatches,
    paymentPlans,
    inventorySessions,
    inventoryAdjustments,
  ] = await Promise.all([
    countDocsBySiteId(COLLECTIONS.productSales, siteId, 'canDeleteSite - productSales'),
    countDocsBySiteId(COLLECTIONS.prestations, siteId, 'canDeleteSite - prestations'),
    countDocsBySiteId(COLLECTIONS.stockMovements, siteId, 'canDeleteSite - stockMovements'),
    countDocsBySiteId(COLLECTIONS.deliveries, siteId, 'canDeleteSite - deliveries'),
    getDocs(
      query(collection(db, COLLECTIONS.deliveries), where('fromSiteId', '==', siteId), limit(1))
    )
      .then((s) => (s.empty ? 0 : 1))
      .catch((err) => {
        handleFirestoreError(err, 'canDeleteSite - deliveries from');
        throw err;
      }),
    countDocsBySiteId(
      COLLECTIONS.externalDispatches,
      siteId,
      'canDeleteSite - externalDispatches'
    ),
    countDocsBySiteId(COLLECTIONS.paymentPlans, siteId, 'canDeleteSite - paymentPlans'),
    countDocsBySiteId(
      COLLECTIONS.inventorySessions,
      siteId,
      'canDeleteSite - inventorySessions'
    ),
    countDocsBySiteId(
      COLLECTIONS.inventoryAdjustments,
      siteId,
      'canDeleteSite - inventoryAdjustments'
    ),
  ]);

  blockers.productSales = productSales;
  blockers.prestations = prestations;
  blockers.stockMovements = stockMovements;
  blockers.deliveries = deliveriesTo + deliveriesFrom > 0 ? 1 : 0;
  blockers.externalDispatches = externalDispatches;
  blockers.paymentPlans = paymentPlans;
  blockers.inventorySessions = inventorySessions;
  blockers.inventoryAdjustments = inventoryAdjustments;

  // Produits : stock réel sur le lieu (map stockBySite)
  const productsSnap = await getDocs(
    query(collection(db, COLLECTIONS.products), where('businessId', '==', businessId))
  ).catch((err) => {
    handleFirestoreError(err, 'canDeleteSite - products');
    throw err;
  });
  let productsWithStock = 0;
  productsSnap.docs.forEach((d) => {
    const data = d.data();
    const qty = Number(data.stockBySite?.[siteId] ?? 0);
    if (qty > 0) productsWithStock += 1;
  });
  blockers.productsWithStock = productsWithStock;

  return evaluateSiteDelete(blockers);
};

/** Supprimer un lieu (Super Admin) si aucune donnée métier liée. */
export const useDeleteSite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      siteId: string;
      businessId: string;
      allowMultiplePointOfSale?: boolean;
    }) => {
      const check = await canDeleteSite(params);
      if (!check.canDelete) {
        throw new Error(check.reason || 'Suppression impossible');
      }

      const { siteId, businessId } = params;

      // Nettoyage références mortes sûres (clés stock à 0, favoris employés)
      const productsSnap = await getDocs(
        query(collection(db, COLLECTIONS.products), where('businessId', '==', businessId))
      ).catch((err) => {
        handleFirestoreError(err, 'useDeleteSite - products cleanup');
        throw err;
      });

      let batch = writeBatch(db);
      let ops = 0;
      const commitBatchIfNeeded = async (force = false) => {
        if (ops === 0) return;
        if (!force && ops < 400) return;
        await batch.commit();
        batch = writeBatch(db);
        ops = 0;
      };

      for (const productDoc of productsSnap.docs) {
        const data = productDoc.data();
        const hasStockKey =
          data.stockBySite != null &&
          Object.prototype.hasOwnProperty.call(data.stockBySite, siteId);
        const hasAlertKey =
          data.minStockAlertBySite != null &&
          Object.prototype.hasOwnProperty.call(data.minStockAlertBySite, siteId);
        if (!hasStockKey && !hasAlertKey) continue;

        const qty = Number(data.stockBySite?.[siteId] ?? 0);
        if (qty > 0) {
          throw new Error(
            'Impossible de supprimer : un produit a encore du stock sur ce lieu.'
          );
        }

        const patch: Record<string, ReturnType<typeof deleteField>> = {};
        if (hasStockKey) patch[`stockBySite.${siteId}`] = deleteField();
        if (hasAlertKey) patch[`minStockAlertBySite.${siteId}`] = deleteField();
        batch.update(productDoc.ref, patch);
        ops += 1;
        await commitBatchIfNeeded();
      }

      const employeesSnap = await getDocs(
        query(collection(db, COLLECTIONS.employees), where('businessId', '==', businessId))
      ).catch((err) => {
        handleFirestoreError(err, 'useDeleteSite - employees cleanup');
        throw err;
      });

      for (const empDoc of employeesSnap.docs) {
        const empData = empDoc.data();
        const patch: Record<string, ReturnType<typeof deleteField> | string[] | ReturnType<typeof Timestamp.now>> = {
          updatedAt: Timestamp.now(),
        };
        let needsUpdate = false;
        if (empData.defaultSiteId === siteId) {
          patch.defaultSiteId = deleteField();
          needsUpdate = true;
        }
        const assigned = Array.isArray(empData.assignedSiteIds)
          ? empData.assignedSiteIds.filter((id: unknown) => typeof id === 'string' && id !== siteId)
          : null;
        if (Array.isArray(empData.assignedSiteIds) && empData.assignedSiteIds.includes(siteId)) {
          if (assigned && assigned.length > 0) {
            patch.assignedSiteIds = assigned;
          } else {
            patch.assignedSiteIds = deleteField();
          }
          needsUpdate = true;
        }
        if (!needsUpdate) continue;
        batch.update(empDoc.ref, patch);
        ops += 1;
        await commitBatchIfNeeded();
      }

      batch.delete(doc(db, COLLECTIONS.sites, siteId));
      ops += 1;
      await commitBatchIfNeeded(true);

      return siteId;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sites', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
};

/** Liste des lieux d'un business. Réservé au Super Admin (affiché uniquement dans l'espace admin). */
export const useSites = (businessId: string | undefined) => {
  return useQuery({
    queryKey: ['sites', businessId],
    queryFn: async () => {
      if (!businessId) return [];

      const q = query(
        collection(db, COLLECTIONS.sites),
        where('businessId', '==', businessId),
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(q).catch((err) => {
        handleFirestoreError(err, 'useSites');
        throw err;
      });

      return snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          businessId: data.businessId,
          name: data.name,
          type: data.type as SiteType,
          order: data.order,
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Site;
      });
    },
    enabled: !!businessId,
  });
};

/** Créer un lieu. À utiliser côté Super Admin uniquement. */
export const useAddSite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      businessId: string;
      name: string;
      type: SiteType;
      order?: number;
      /** Si true (mode any_site), plusieurs points de vente sont autorisés. */
      allowMultiplePointOfSale?: boolean;
    }) => {
      // Règle métier : un seul Point de vente par business (sauf mode vente multi-lieux)
      if (params.type === 'point_of_sale' && !params.allowMultiplePointOfSale) {
        const existingPosSnap = await getDocs(
          query(
            collection(db, COLLECTIONS.sites),
            where('businessId', '==', params.businessId),
            where('type', '==', 'point_of_sale')
          )
        );
        if (!existingPosSnap.empty) {
          throw new Error('Un seul lieu de type "Point de vente / Boutique" est autorisé par business.');
        }
      }

      const ref = collection(db, COLLECTIONS.sites);
      const now = Timestamp.now();
      const docRef = await addDoc(ref, {
        businessId: params.businessId,
        name: params.name.trim(),
        type: params.type,
        order: params.order ?? 0,
        isActive: true,
        createdAt: now,
        updatedAt: now,
      });
      return docRef.id;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sites', variables.businessId] });
    },
  });
};

/** Modifier un lieu. À utiliser côté Super Admin uniquement. */
export const useUpdateSite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      siteId: string;
      businessId: string;
      name?: string;
      type?: SiteType;
      order?: number;
      isActive?: boolean;
      /** Si true (mode any_site), plusieurs points de vente sont autorisés. */
      allowMultiplePointOfSale?: boolean;
    }) => {
      const ref = doc(db, COLLECTIONS.sites, params.siteId);
      const siteSnap = await getDoc(ref);
      if (!siteSnap.exists()) {
        throw new Error('Lieu introuvable');
      }
      const currentSite = siteSnap.data() as any;
      const allowMultiple = params.allowMultiplePointOfSale === true;

      // Règle métier : un seul Point de vente par business (sauf mode vente multi-lieux)
      if (
        params.type === 'point_of_sale' &&
        currentSite.type !== 'point_of_sale' &&
        !allowMultiple
      ) {
        const existingPosSnap = await getDocs(
          query(
            collection(db, COLLECTIONS.sites),
            where('businessId', '==', params.businessId),
            where('type', '==', 'point_of_sale')
          )
        );
        const hasAnotherPos = existingPosSnap.docs.some((d) => d.id !== params.siteId);
        if (hasAnotherPos || (!existingPosSnap.empty && currentSite.type !== 'point_of_sale')) {
          throw new Error('Un seul lieu de type "Point de vente / Boutique" est autorisé par business.');
        }
      }

      // Empêcher de désactiver le seul point de vente existant (mode pos_only)
      if (
        params.isActive === false &&
        currentSite.type === 'point_of_sale' &&
        !allowMultiple
      ) {
        throw new Error('Le point de vente principal ne peut pas être désactivé.');
      }

      // Empêcher de convertir le seul point de vente vers un autre type (mode pos_only)
      if (
        params.type &&
        currentSite.type === 'point_of_sale' &&
        params.type !== 'point_of_sale' &&
        !allowMultiple
      ) {
        throw new Error('Le point de vente principal ne peut pas être converti vers un autre type.');
      }

      const updateData: Record<string, string | number | boolean | ReturnType<typeof Timestamp.now>> = { updatedAt: Timestamp.now() };
      if (params.name !== undefined) updateData.name = params.name.trim();
      if (params.type !== undefined) updateData.type = params.type;
      if (params.order !== undefined) updateData.order = params.order;
      if (params.isActive !== undefined) updateData.isActive = params.isActive;
      await updateDoc(ref, updateData);
      return params.siteId;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sites', variables.businessId] });
    },
  });
};

/**
 * Lieu opérationnel par défaut (stock / vente / filtres).
 * Priorité : lieu favori employé (s’il est actif) → résolution business (POS / premier lieu).
 */
export const useDefaultPointOfSaleSite = (businessId: string | undefined) => {
  const { data: sites = [] } = useSites(businessId);
  const { data: business } = useBusiness(businessId || '');
  const { currentBusiness } = useAuth();
  const { data: employee } = useCurrentEmployee();
  const resolvedBusiness =
    business ??
    (currentBusiness?.id === businessId ? currentBusiness : null);

  if (employee?.businessId === businessId) {
    const favorite = resolveFavoriteSite(sites, employee?.defaultSiteId);
    if (favorite) return favorite;
  }

  return resolveDefaultSalesOrStockSite(sites, resolvedBusiness);
};

/** Lieux actifs utilisables comme sortie de stock à la vente. */
export const useActiveSalesSites = (businessId: string | undefined) => {
  const { data: sites = [], isLoading, error, refetch } = useSites(businessId);
  const activeSitesList = sites.filter((s) => s.isActive);
  return { sites: activeSitesList, allSites: sites, isLoading, error, refetch };
};

/** Premier lieu de type entrepôt actif — pour site de réception par défaut (UI). */
export const useDefaultWarehouseSite = (businessId: string | undefined) => {
  const { data: sites = [] } = useSites(businessId);
  return sites.find((s) => s.type === 'warehouse' && s.isActive) ?? null;
};

/**
 * Lieu de réception par défaut (approvisionnements).
 * Priorité : lieu favori employé → entrepôt / POS selon le mode business.
 */
export const useDefaultReceivingSite = (businessId: string | undefined) => {
  const { data: sites = [] } = useSites(businessId);
  const { data: business } = useBusiness(businessId || '');
  const { currentBusiness } = useAuth();
  const { data: employee } = useCurrentEmployee();
  const resolvedBusiness =
    business ??
    (currentBusiness?.id === businessId ? currentBusiness : null);

  if (employee?.businessId === businessId) {
    const favorite = resolveFavoriteSite(sites, employee?.defaultSiteId);
    if (favorite) return favorite;
  }

  return resolveDefaultReceivingSite(sites, resolvedBusiness);
};
